import * as React from 'react';
import {
  BottomSheet,
  BottomSheetClose,
  BottomSheetContent,
  Button,
  Form,
  FormField,
  Input,
  PhoneInput,
  Stack,
  applyServerErrors,
  useZodForm,
} from '@relay-towing/ui';
import {
  useCompanyInfo,
  useUpdateCompany,
  type UpdateCompanyPayload,
} from '../../api/hooks/useCompanyInfo';
import {
  businessProfileSchema,
  type BusinessProfileValues,
} from '../../lib/businessProfileSchema';
import {
  buildUpdateBody,
  formatCompanyAddress,
  snapshotCompanyFields,
  type ParsedAddressComponents,
} from '../../lib/companyAddress';
import { AddressInput } from '../shell/AddressInput';

const FORM_ID = 'business-profile';

/**
 * The setup checklist's Business profile, the Figma's sheet over it
 * (123:7218, note 123:7249: dismissible, and only as tall as its fields):
 * the company's name, address and phone, saved to `PATCH /companies/:id`
 * as the dashboard's company settings save them, then Save or Skip for now.
 * A place picked from the list is saved as its street, city, state and
 * zip; an address typed over replaces the street and clears the rest.
 */
export function BusinessProfileSheet({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const company = useCompanyInfo();
  const updateCompany = useUpdateCompany();
  const [picked, setPicked] = React.useState<ParsedAddressComponents | null>(
    null
  );
  const form = useZodForm(businessProfileSchema, {
    defaultValues: { name: '', companyPhone: '', addressLine: '' },
  });
  const initial = company.data ? snapshotCompanyFields(company.data) : null;

  // Filled from the company each time the sheet opens, so a second visit
  // shows what was saved rather than what was typed and skipped.
  const { reset } = form;
  React.useEffect(() => {
    if (!open || !company.data) return;
    const saved = snapshotCompanyFields(company.data);
    reset({
      name: saved.name,
      companyPhone: saved.companyPhone,
      addressLine: formatCompanyAddress(
        saved.streetAddress,
        saved.city,
        saved.state,
        saved.zipCode
      ),
    });
    setPicked(null);
  }, [open, company.data, reset]);

  const save = async (values: BusinessProfileValues) => {
    if (!initial) {
      // Nothing to save over until the company has loaded.
      form.setError('root.server', {
        message: 'The business profile has not loaded yet. Try again.',
      });
      return;
    }
    const payload: UpdateCompanyPayload = picked
      ? {
          name: values.name.trim(),
          companyPhone: values.companyPhone.trim(),
          ...picked,
        }
      : buildUpdateBody(
          values.name,
          values.companyPhone,
          values.addressLine,
          initial
        );
    try {
      await updateCompany.mutateAsync(payload);
      onOpenChange(false);
    } catch (error) {
      applyServerErrors(
        form,
        error,
        'The business profile could not be saved.'
      );
    }
  };

  return (
    <BottomSheet open={open} onOpenChange={onOpenChange}>
      <BottomSheetContent
        title="Business profile"
        description="Review your name and location"
        prominent
        footer={
          <Stack gap={4}>
            <Button
              type="submit"
              form={FORM_ID}
              size="xl"
              pill
              variant="soft"
              loading={updateCompany.isPending}
            >
              Save
            </Button>
            <BottomSheetClose asChild>
              <Button variant="link">Skip for now</Button>
            </BottomSheetClose>
          </Stack>
        }
      >
        <Form id={FORM_ID} form={form} gap={4} onSubmit={save}>
          <FormField form={form} name="name" label="Business Name">
            {(field) => <Input {...field} placeholder="Your company name" />}
          </FormField>
          <FormField form={form} name="addressLine" label="Business Address">
            {(field) => (
              <AddressInput
                value={field.value}
                onChange={field.onChange}
                placeholder="Street, City, State, ZIP"
                onPlace={(place) => setPicked(place?.parts ?? null)}
              />
            )}
          </FormField>
          <FormField form={form} name="companyPhone" label="Phone Number">
            {(field) => <PhoneInput {...field} />}
          </FormField>
        </Form>
      </BottomSheetContent>
    </BottomSheet>
  );
}
